import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { PrimeNGConfig } from 'primeng/api';
import { LanguageService } from 'src/app/sharedFeatures/services/language';
import { HubNotifictionService } from 'src/app/sharedFeatures/services/HubNotifiction.service';
import { NavItemModel } from '../../../../sharedFeatures/models/nav-item-model';
import { UserLoggedIn } from '../../../../sharedFeatures/models/user-login.model';
import { CurrentUserService } from '../../../../sharedFeatures/services/current-user.service';
import { NavigationService } from '../../../../sharedFeatures/services/navigation.service';
import { Subscription } from 'rxjs';
import { ExchangeToken } from 'src/app/modules/users/models/exchangeToken';
import { LoginService } from 'src/app/modules/users/services/login.service';
import { LookupService } from '../../../../sharedFeatures/services/lookups-service';
import { UmRole } from 'src/app/modules/roles/models/role-enum';
import { AutomaticTitleService } from '../../../../sharedFeatures/services/automatic-title.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'NCRSAPP';
  directionRTL: boolean = true;
  isMenuCollapsed: boolean = false;
  isNavigating: boolean = false;
  currentUser: UserLoggedIn | null = null;
  private languageChangeSubscription: Subscription;
  subscriptions: Subscription[] = [];
  refresh: boolean = false;
  private isTokenExchanged: boolean = false;
  private isRoleBasedNavigation: boolean = false;
  isUserManagement: boolean;
  navList: NavItemModel[] = [];
  isAuthPage: boolean = false; // Track if we're on login/forget-password/landing pages
  private readonly landingPaths = [
    '/about-us',
    '/our-products',
    '/solutions',
    '/services',
    '/work-with-us',
    '/international',
    '/careers',
    '/contact-us',
    '/terms-and-conditions',
    '/privacy-policy',
  ];

  private isChromelessUrl(url: string): boolean {
    if (!url) return false;
    const path = url.split('?')[0];
    if (path === '/' || path === '') return true;
    return (
      path.includes('/login') ||
      path.includes('/forget-password') ||
      path.includes('/modules') ||
      this.landingPaths.some(p => path.startsWith(p))
    );
  }
  constructor(
    public translateService: TranslateService,
    public HubNotifictionService: HubNotifictionService,
    private currentUserService: CurrentUserService,
    private router: Router,
    private route: ActivatedRoute,
    public languageService: LanguageService,
    private navigationService: NavigationService,
    private loginService: LoginService,
    private cdr: ChangeDetectorRef,
    private lookupService: LookupService,
    private automaticTitleService: AutomaticTitleService,
    private primengConfig: PrimeNGConfig
  ) {
    this.languageChangeSubscription = this.languageService.language.subscribe(
      (lang: string) => {
        // Mirror the language onto the <html> element so body-level overlays
        // (PrimeNG dynamic dialogs, dropdown panels, tooltips, toasts) inherit
        // the correct `dir`. The app-component's inner [dir] only covers
        // descendants of <app-root>, not portals rendered at <body>.
        // (Previously this handler called ngOnDestroy() which unsubscribed
        // router events that keep currentUser / isAuthPage in sync — that
        // destructive behavior has been removed.)
        if (typeof lang === 'string') {
          const isRtl = lang === 'ar';
          document.documentElement.setAttribute('dir', isRtl ? 'rtl' : 'ltr');
          document.documentElement.lang = lang;
        }
        // Apply PrimeNG's own translations (filter operators, dropdown
        // "No results found" / empty messages, etc.) app-wide so they are
        // localized even on screens that never mount a data grid.
        this.applyPrimeNgTranslation();
      }
    );

    translateService.addLangs(['en', 'ar']);
    const currentLang = localStorage.getItem('currentLang');
    if (currentLang) {
      this.languageService.setLanguage(currentLang);
      this.translateService.use(currentLang);
    } else {
      this.languageService.setLanguage('en');
      translateService.use('en');
    }

    // Re-apply PrimeNG translations whenever a language file finishes loading
    // (onLangChange fires after the JSON is fetched, so the strings exist).
    this.languageChangeSubscription.add(
      this.translateService.onLangChange.subscribe(() =>
        this.applyPrimeNgTranslation()
      )
    );
    this.applyPrimeNgTranslation();
  }

  // ngOnInit(): void {

  //   const queryParams = new URLSearchParams(window.location.search);
  //   const token1 = queryParams.get('token');
  //   console.log('token1', token1);
  //   this.route.queryParams.subscribe(params => {
  //     let token = params['token'] ? params['token'] : token1;
  //     //let userString = localStorage.getItem("currentUser");
  //     //  &&(userString==undefined||userString==null)
  //     if (token) {
  //      // //;
  //       let exchangeToken: ExchangeToken = { token: token };
  //       this.loginService.exchangeToken(exchangeToken).subscribe(
  //         (res: any) => {
  //           this.currentUser = res;
  //           this.currentUserService.setCurrentUser(res);
  //           this.cdr.detectChanges(); // Trigger manual change detection
  //           this.removeQueryParam();
  //           localStorage.setItem('currentUser', JSON.stringify(res));
  //           this.refresh = true;

  //           // this.lookupService.updateCache();

  //           this.router.navigate(['/section/list']);
  //         },
  //         (error: any) => {
  //         },
  //         () => { }
  //       )
  //     } else {
  //       // this.removeQueryParam();
  //     }
  //   });
  //   this.router.events.subscribe((event: any) => {
  //     this.isUserModule(event.url);

  //     this.currentUser = this.currentUserService.getCurrentUser();
  //     this.HubNotifictionService.userId = this.currentUser?.id;

  //   });
  // }

  ngOnInit(): void {
    // Check if this is the first page load (no session storage flag exists)
    // This handles fresh visits, new tabs, or after browser restart
    const currentUrl = this.router.url;
    const isAuthPage = this.isChromelessUrl(currentUrl);

    // Only reload on non-auth pages on first visit
    if (!isAuthPage && !sessionStorage.getItem('appInitialized')) {
      sessionStorage.setItem('appInitialized', 'true');
      setTimeout(() => {
        window.location.reload();
      }, 5);
      return;
    }

    // Set the flag if not already set (for auth pages)
    if (!sessionStorage.getItem('appInitialized')) {
      sessionStorage.setItem('appInitialized', 'true');
    }

    // Initialize automatic title updates
    this.automaticTitleService.initialize();

    // Initialize auth page check
    this.isAuthPage = this.isChromelessUrl(this.router.url);

    const queryParamsSub = this.route.queryParams.subscribe(params => {
      const token =
        params['token'] ||
        new URLSearchParams(window.location.search).get('token');
      if (token && !this.isTokenExchanged) {
        this.currentUserService.setCurrentUser(null);
        localStorage.removeItem('currentUser');
        const exchangeToken: ExchangeToken = { token: token };
        this.isTokenExchanged = true;
        this.loginService.exchangeToken(exchangeToken).subscribe(
          (res: UserLoggedIn) => {
            this.currentUserService.setCurrentUser(res);
            this.currentUser = res;
            this.refresh = true;
            //this.removeQueryParam();

            // Navigate based on module ID after token exchange
            this.isRoleBasedNavigation = true; // Set flag to prevent router events override

            if (window.location.href.includes('system-code')) {
              this.router.navigate(['/system-code/list']);
            } else if (window.location.href.includes('user-management')) {
              this.router.navigate(['/user-management/users/list']);
            } else if (res.moduleId === 2) {
              if (res.roleId == UmRole.ProjectHead) {
                this.router.navigate([
                  '/msm-checklist/general-dashboard/divisions',
                ]);
              } else if (res.roleId == UmRole.Agent) {
                this.router.navigate(['/msm-checklist/agent-ui/dashboard']);
              } else if (res.roleId == UmRole.Stakeholder) {
                this.router.navigate([
                  '/msm-checklist/general-dashboard/divisions',
                ]);
              } else if (res.roleId == UmRole.ProjectAdmin) {
                this.router.navigate(['/msm-checklist/project-admin-ui']);
              } else {
                // For other roles in moduleId 2, go to the landing page
                this.router.navigate(['/msm-checklist/action-plan/list']);
              }
            } else {
              this.router.navigate(['/dashboard']);
            }

            // Reset flag after navigation completes
            setTimeout(() => {
              this.isRoleBasedNavigation = false;
            }, 1000);
          },
          (error: any) => {}
        );
      }
    });
    const sub = this.router.events.subscribe((event: any) => {
      //;
      // this.isUserModule(event.url);

      this.currentUser = this.currentUserService.getCurrentUser();
      this.HubNotifictionService.userId = this.currentUser?.id;

      // Check if we're on an authentication / public landing page (no app chrome)
      if (event instanceof NavigationEnd) {
        this.isAuthPage = this.isChromelessUrl(event.url);
      }

      // Skip router events override during role-based navigation
      if (this.isRoleBasedNavigation) {
        return;
      }

      // Handle navigation for existing users (not token exchange)
      // Only redirect to mystery-shopper landing page if:
      // 1. Not during role-based navigation
      // 2. User is trying to access /dashboard
      // 3. User has moduleId === 2 but no specific role navigation was set
      if (
        event instanceof NavigationEnd &&
        event.url === '/dashboard' &&
        this.currentUser &&
        this.currentUser.moduleId === 2
      ) {
        // Navigate based on role
        if (this.currentUser.roleId == UmRole.ProjectHead) {
          this.router.navigate(['/msm-checklist/general-dashboard/divisions']);
        } else if (this.currentUser.roleId == UmRole.Agent) {
          this.router.navigate(['/msm-checklist/agent-ui/dashboard']);
        } else if (this.currentUser.roleId == UmRole.Stakeholder) {
          this.router.navigate(['/msm-checklist/general-dashboard/divisions']);
        } else if (this.currentUser.roleId == UmRole.ProjectAdmin) {
          this.router.navigate(['/msm-checklist/project-admin-ui']);
        } else {
          // For other roles, go to landing page
          this.router.navigate(['/msm-checklist/action-plan/list']);
        }
      }
      // else if ((event.route && event.route.path && event.route?.path.contains('system-code')) || event.url.contains('system-code')) {

      //   this.router.navigate(['/system-code/list']);
      // }
      // else if ((event.route && event.route.path && event.route?.path.contains('user-management')) || event.url.contains('user-management')) {

      //   this.router.navigate(['/user-management/users/list']);
      // }
    });

    this.subscriptions.push(queryParamsSub);
    this.subscriptions.push(sub);
  }

  removeQueryParam() {
    // //;
    const queryParams = { ...this.route.snapshot.queryParams };
    delete queryParams['token'];

    // Update the URL with the modified query parameters
    // Update the URL with the modified query parameters
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      replaceUrl: true,
    });
  }

  // getNavItems() {
  //   this.navigationService.getNavItems().subscribe(res => {
  //     this.navList = res;
  //   });
  // }

  goTop() {
    // The page scrolls inside the `.app` container (so the scrollbar can sit on
    // the correct edge per language direction), not on <body>/<html>. Scroll
    // that container; keep document fallbacks for any view that still relies on
    // page-level scroll.
    const appScroller = document.querySelector('.app') as HTMLElement | null;
    appScroller?.scrollTo({ top: 0, behavior: 'smooth' });
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  }

  switchLang(lang: string) {
    this.translateService.use(lang);
    localStorage.setItem('currentLang', lang);
  }

  /**
   * Push the active language's PrimeNG translation strings into PrimeNGConfig
   * so dropdown/calendar/filter built-in messages (e.g. "No results found")
   * follow the app language everywhere, not just on grid pages.
   */
  private applyPrimeNgTranslation(): void {
    this.translateService.get('primeng.filter').subscribe((res: any) => {
      if (res && typeof res === 'object') {
        this.primengConfig.setTranslation(res);
      }
    });
  }
  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
    //this.ngOnInit();
  }

  isUserModule(url: string) {
    //;
    if (url) {
      // Logic to check if the current route belongs to the admin module
      const adminRoutes = ['/system-code']; // Define admin-specific routes
      this.isUserManagement = adminRoutes.some(route => url.startsWith(route));
      //return true;
    }
  }
}
